<div class="workspace-container" [class.search-mode]="showSearchConsole">

  <!-- ═══════════════════════════════════════════════════════════════════
       TOP HEADER BAR (always visible)
  ═══════════════════════════════════════════════════════════════════ -->
  <header class="workspace-header">
    <div class="header-left">
      <button class="btn-back" (click)="showSearchConsole ? closeSearchConsole() : backToDashboard()">
        <i class="bi bi-arrow-left"></i>
        {{ showSearchConsole ? 'Back to Workspace' : 'Dashboard' }}
      </button>
      <div class="client-title" *ngIf="profile">
        <span class="client-name">{{ profile.basic_info?.first_name || profile.basic_info?.name || 'Express Client' }} {{ profile.basic_info?.last_name || '' }}</span>
        <span class="client-id">#{{ profile._id?.toString()?.slice(-6)?.toUpperCase() }}</span>
        <span class="plan-badge express-badge"><i class="bi bi-stars"></i> EXPRESS</span>
        <span *ngIf="showSearchConsole" class="console-label"><i class="bi bi-sliders2"></i> Matchmaking Console</span>
      </div>
    </div>

    <div class="header-center" *ngIf="!showSearchConsole && profile">
      <div class="stage-updater">
        <label>Stage:</label>
        <select [(ngModel)]="selectedStage" class="stage-select">
          <option *ngFor="let st of stages" [value]="st">{{ st }}</option>
        </select>
        <input type="text" placeholder="Optional note..." [(ngModel)]="stageUpdateNote" class="stage-note-input" />
        <button class="btn-update-stage" (click)="updateStage()">Update</button>
      </div>
    </div>

    <div class="header-right">
      <button *ngIf="!showSearchConsole" class="btn-search-profiles" (click)="openSearchConsole()" id="btn-open-search-console">
        <i class="bi bi-person-lines-fill"></i> Search Profiles for Client
      </button>
      <div *ngIf="showSearchConsole && selectedProfilesForShare.length > 0" class="share-count-pill">
        <span><strong>{{ selectedProfilesForShare.length }}</strong> selected</span>
        <button class="btn-do-share" (click)="confirmShareProfiles()" [disabled]="sendingChat">
          <i class="bi" [ngClass]="sendingChat ? 'bi-hourglass-split' : 'bi-send-fill'"></i>
          {{ sendingChat ? 'Sharing...' : 'Share to Client' }}
        </button>
      </div>
    </div>
  </header>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="spinner-border text-danger" role="status"></div>
    <p>Loading client workspace...</p>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════
       NORMAL 3-PANEL WORKSPACE VIEW
  ═══════════════════════════════════════════════════════════════════ -->
  <main class="workspace-grid" [class.dossier-active]="activeWorkspaceTab === 'profile'" *ngIf="!loading && profile && !showSearchConsole">

    <!-- Panel 1: Left — Profile & Contact -->
    <aside class="panel profile-panel">
      <div class="profile-snapshot">
        <div class="avatar-big">
          <!-- Loaded via base64 proxy (avatar_Id is a server file path) -->
          <img *ngIf="base64Images[profile._id]"
               [src]="'data:image/jpeg;base64,' + base64Images[profile._id]" alt="Avatar" />
          <!-- Direct URL fallback -->
          <img *ngIf="!base64Images[profile._id] && profile.personal_info?.avatar_Id && (profile.personal_info.avatar_Id.startsWith('http') || profile.personal_info.avatar_Id.startsWith('data:'))"
               [src]="profile.personal_info.avatar_Id" alt="Avatar" />
          <!-- Letter initial fallback -->
          <span *ngIf="!base64Images[profile._id] && (!profile.personal_info?.avatar_Id || (!profile.personal_info.avatar_Id.startsWith('http') && !profile.personal_info.avatar_Id.startsWith('data:')))">
            {{ (profile.basic_info?.first_name || profile.basic_info?.name || 'C').charAt(0).toUpperCase() }}
          </span>
        </div>
        <h3>{{ profile.basic_info?.first_name || profile.basic_info?.name || 'Express Client' }} {{ profile.basic_info?.last_name || '' }}</h3>
        <p>{{ profile.basic_info?.age || '—' }} Yrs, {{ profile.basic_info?.gender || '' }}</p>
        <span class="loc-badge"><i class="bi bi-geo-alt-fill text-danger"></i> {{ profile.residing_location?.city_id?.name || 'India' }}</span>
        
        <!-- View Client's Full Info Button (Existing Profile View Component) -->
        <button class="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#B01E3E] to-rose-700 hover:from-rose-800 hover:to-rose-900 text-white font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-2 mt-4.5 mb-2 border border-rose-600/30 cursor-pointer"
                (click)="activeWorkspaceTab = 'profile'">
          <i class="bi bi-person-badge-fill text-amber-300 text-sm"></i> View Client Full Profile Info
        </button>
      </div>

      <div class="contact-box">
        <h5><i class="bi bi-shield-lock-fill"></i> Direct Contact (RM Only)</h5>
        <div class="c-row"><i class="bi bi-telephone-fill"></i><span>Mobile:</span><strong>{{ profile.basic_info?.mobile || profile.basic_info?.phone || 'N/A' }}</strong></div>
        <div class="c-row"><i class="bi bi-envelope-fill"></i><span>Email:</span><strong class="truncate">{{ profile.basic_info?.email || 'N/A' }}</strong></div>
        <div class="c-row" *ngIf="profile.contact_details?.whatsapp_number"><i class="bi bi-whatsapp text-success"></i><span>WhatsApp:</span><strong class="text-success">{{ profile.contact_details.whatsapp_number }}</strong></div>
      </div>


      <div class="partner-prefs-box" *ngIf="clientPartnerPrefs">
        <div class="flex items-center justify-between mb-3 pb-2 border-b border-rose-100">
          <h5 class="m-0 font-bold text-gray-900 flex items-center gap-1.5"><i class="bi bi-heart-fill text-[#B01E3E]"></i> Partner Preferences</h5>
          <span class="bg-rose-100 text-[#B01E3E] text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider">ACTIVE</span>
        </div>
        
        <div class="space-y-2 text-xs mb-4 max-h-[400px] overflow-y-auto pr-1">
          <!-- Age Range -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="(clientPartnerPrefs.age_from || clientPartnerPrefs.personal_preference?.age_from)">
            <span class="lbl text-gray-500 font-medium">Age Range</span>
            <span class="val font-bold text-gray-900">{{ clientPartnerPrefs.age_from || clientPartnerPrefs.personal_preference?.age_from }} – {{ clientPartnerPrefs.age_to || clientPartnerPrefs.personal_preference?.age_to || 'Any' }} Yrs</span>
          </div>

          <!-- Height -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="(clientPartnerPrefs.height_from || clientPartnerPrefs.personal_preference?.height_from)">
            <span class="lbl text-gray-500 font-medium">Height</span>
            <span class="val font-bold text-gray-900">{{ clientPartnerPrefs.height_from || clientPartnerPrefs.personal_preference?.height_from }} – {{ clientPartnerPrefs.height_to || clientPartnerPrefs.personal_preference?.height_to || '' }}</span>
          </div>

          <!-- Marital Status -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.marital_status || clientPartnerPrefs.personal_preference?.marital_status)">
            <span class="lbl text-gray-500 font-medium">Marital Status</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.marital_status || clientPartnerPrefs.personal_preference?.marital_status) }}</span>
          </div>

          <!-- Religion -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.religion_id || clientPartnerPrefs.religious_preference?.religion_id, 'religion')">
            <span class="lbl text-gray-500 font-medium">Religion</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.religion_id || clientPartnerPrefs.religious_preference?.religion_id, 'religion') }}</span>
          </div>

          <!-- Caste -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.caste_id || clientPartnerPrefs.religious_preference?.caste_id, 'caste')">
            <span class="lbl text-gray-500 font-medium">Caste</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.caste_id || clientPartnerPrefs.religious_preference?.caste_id, 'caste') }}</span>
          </div>

          <!-- Sub Caste -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.sub_caste_id || clientPartnerPrefs.religious_preference?.sub_caste_id, 'subcaste')">
            <span class="lbl text-gray-500 font-medium">Sub-Caste</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.sub_caste_id || clientPartnerPrefs.religious_preference?.sub_caste_id, 'subcaste') }}</span>
          </div>

          <!-- Dietary / Food -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.food || clientPartnerPrefs.lifestyle_and_food_preference?.food)">
            <span class="lbl text-gray-500 font-medium">Diet / Food</span>
            <span class="val font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">{{ getPrefDisplay(clientPartnerPrefs.food || clientPartnerPrefs.lifestyle_and_food_preference?.food) }}</span>
          </div>

          <!-- Smoking / Drinking -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.smoking || clientPartnerPrefs.lifestyle_and_food_preference?.smoking || clientPartnerPrefs.drinking || clientPartnerPrefs.lifestyle_and_food_preference?.drinking)">
            <span class="lbl text-gray-500 font-medium">Smoking/Drinking</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.smoking || clientPartnerPrefs.lifestyle_and_food_preference?.smoking || 'No') }} / {{ getPrefDisplay(clientPartnerPrefs.drinking || clientPartnerPrefs.lifestyle_and_food_preference?.drinking || 'No') }}</span>
          </div>

          <!-- Education -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.education_id || clientPartnerPrefs.education_preference?.education_id, 'education')">
            <span class="lbl text-gray-500 font-medium">Education</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.education_id || clientPartnerPrefs.education_preference?.education_id, 'education') }}</span>
          </div>

          <!-- Occupation -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.occupation_id || clientPartnerPrefs.profession_preference?.occupation_id, 'occupation')">
            <span class="lbl text-gray-500 font-medium">Occupation</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.occupation_id || clientPartnerPrefs.profession_preference?.occupation_id, 'occupation') }}</span>
          </div>

          <!-- City / Location -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.city_id || clientPartnerPrefs.location_preference?.city_id, 'city')">
            <span class="lbl text-gray-500 font-medium">Location</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.city_id || clientPartnerPrefs.location_preference?.city_id, 'city') }}</span>
          </div>

          <!-- State -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.state_id || clientPartnerPrefs.location_preference?.state_id, 'state')">
            <span class="lbl text-gray-500 font-medium">State</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.state_id || clientPartnerPrefs.location_preference?.state_id, 'state') }}</span>
          </div>

          <!-- Country -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.country_id || clientPartnerPrefs.location_preference?.country_id, 'country')">
            <span class="lbl text-gray-500 font-medium">Country</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.country_id || clientPartnerPrefs.location_preference?.country_id, 'country') }}</span>
          </div>

          <!-- Mother Tongue / Language -->
          <div class="detail-item flex justify-between items-center py-1 border-b border-gray-100/80" *ngIf="getPrefDisplay(clientPartnerPrefs.mother_tongue_id || clientPartnerPrefs.personal_preference?.language_id, 'language')">
            <span class="lbl text-gray-500 font-medium">Mother Tongue</span>
            <span class="val font-bold text-gray-900">{{ getPrefDisplay(clientPartnerPrefs.mother_tongue_id || clientPartnerPrefs.personal_preference?.language_id, 'language') }}</span>
          </div>
          
          <!-- Fallback indicator if fields are custom or minimal -->
          <div class="detail-item flex justify-between items-center py-1 text-gray-400 italic" *ngIf="!(clientPartnerPrefs.age_from || clientPartnerPrefs.personal_preference?.age_from || clientPartnerPrefs.religion_id || clientPartnerPrefs.religious_preference?.religion_id || clientPartnerPrefs.education_id || clientPartnerPrefs.education_preference?.education_id || clientPartnerPrefs.food || clientPartnerPrefs.lifestyle_and_food_preference?.food)">
            <span><i class="bi bi-info-circle mr-1"></i> Custom preference profile active</span>
            <span class="text-[11px]">Click below to run</span>
          </div>
        </div>
        
        <!-- Run Partner Prefs & Populate Profiles Button -->
        <button class="w-full py-3 px-3.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-950 font-black text-xs uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                (click)="runClientPartnerPreferencesSearch()">
          <i class="bi bi-magic text-sm"></i> Run Partner Prefs & Populate Matches
        </button>
      </div>
      <div class="partner-prefs-box bg-gray-50 border border-gray-200 p-4 rounded-xl mt-4 text-center" *ngIf="!clientPartnerPrefs">
        <i class="bi bi-heart text-gray-400 text-2xl mb-1 block"></i>
        <h6 class="text-xs font-bold text-gray-700 m-0">No Partner Preferences Saved</h6>
        <p class="text-[11px] text-gray-500 m-0 mt-1">Client hasn't configured specific criteria yet.</p>
        <button class="w-full py-2.5 px-3 rounded-xl bg-[#B01E3E] text-white font-bold text-xs shadow-2xs transition-all flex items-center justify-center gap-1.5 mt-3 cursor-pointer"
                (click)="openSearchConsole(true)">
          <i class="bi bi-search"></i> Manual Search Console
        </button>
      </div>
    </aside>

    <!-- Panel 2: Center — Concierge Chat (Hidden when in Full Dossier View) -->
    <section class="panel chat-panel" *ngIf="activeWorkspaceTab !== 'profile'">
      <div class="panel-header">
        <h4><i class="bi bi-chat-dots-fill text-danger"></i> Concierge Client Chat</h4>
        <button class="btn-share-profile" (click)="openSearchConsole()" id="btn-chat-search-profiles">
          <i class="bi bi-person-plus-fill"></i> Search & Recommend Profiles
        </button>
      </div>

      <div class="chat-messages-area">
        <div *ngIf="messages.length === 0" class="empty-chat">
          <i class="bi bi-chat-heart"></i>
          <p>No messages yet. Click "Search & Recommend Profiles" to find and share matches.</p>
        </div>

        <div *ngFor="let msg of messages" class="chat-bubble" [ngClass]="msg.sender_type === 'RM' ? 'from-rm' : 'from-client'">
          <div class="bubble-header">
            <span class="sender">{{ msg.sender_type === 'RM' ? 'You (Concierge RM)' : getClientFirstName() }}</span>
            <span class="time">{{ msg.created_at | date:'shortTime' }}</span>
          </div>

          <!-- RM Note displayed above profile cards -->
          <div *ngIf="msg.rm_note" class="rm-note-bubble">
            <i class="bi bi-quote"></i> {{ msg.rm_note }}
          </div>

          <div class="bubble-content" *ngIf="msg.content && msg.message_type !== 'PROFILE_CARD'">{{ msg.content }}</div>

          <div class="shared-cards-grid" *ngIf="msg.message_type === 'PROFILE_CARD' && msg.shared_profiles?.length > 0">
            <div *ngFor="let card of msg.shared_profiles" class="shared-match-card">
              <div class="card-img">
                <img *ngIf="card.photo_url" [src]="card.photo_url" alt="Match" />
                <span *ngIf="!card.photo_url">{{ card.name?.charAt(0) || 'M' }}</span>
              </div>
              <div class="card-info">
                <h6>{{ card.name }}</h6>
                <p>{{ card.age }} Yrs • {{ card.occupation }} • {{ card.location }}</p>
              </div>
              <div class="card-actions">
                <span class="card-score"><i class="bi bi-stars"></i> {{ card.compatibility_score }}% Match</span>
                <button *ngIf="msg.sender_type === 'RM'" class="btn-interest-on-behalf" (click)="sendInterestOnBehalf(card.profile_id || card._id)">
                  <i class="bi bi-heart-fill"></i> Send Interest
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-input-box">
        <textarea [(ngModel)]="chatMessageText" (keydown.enter)="$event.preventDefault(); sendChatMessage()" placeholder="Type a message..." rows="2" class="form-control flex-1"></textarea>
        <button class="btn-send" (click)="sendChatMessage()" [disabled]="sendingChat || !chatMessageText.trim()">
          <i class="bi bi-send-fill"></i>
        </button>
      </div>
    </section>

    <!-- Full-Width Comprehensive Dossier Stage (Takes over Center + Right space when activeWorkspaceTab === 'profile') -->
    <section *ngIf="activeWorkspaceTab === 'profile'" class="panel flex-1 bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex flex-col min-h-[85vh]">
      <!-- Minimal top action bar for navigating or searching matches without cluttering the profile view -->
      <div class="bg-white border-b border-gray-200/80 px-6 py-3 flex items-center justify-between text-xs">
        <button class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-xl flex items-center gap-2 transition-all cursor-pointer shadow-2xs"
                (click)="activeWorkspaceTab = 'tasks'">
          <i class="bi bi-arrow-left text-[#B01E3E]"></i> Back to Chat & Workspace
        </button>
        <button class="px-4 py-2 bg-[#B01E3E] hover:bg-rose-900 text-white font-bold rounded-xl flex items-center gap-2 transition-all cursor-pointer shadow-2xs"
                (click)="openSearchConsole()">
          <i class="bi bi-person-plus-fill text-amber-300"></i> Search & Recommend Matches
        </button>
      </div>
      <div class="flex-1 overflow-y-auto p-6 bg-slate-50/50 max-h-[calc(100vh-140px)]">
        <div class="w-full mx-auto">
          <app-profile-details [profileId]="clientId" [isEmbedded]="true"></app-profile-details>
        </div>
      </div>
    </section>

    <!-- Panel 3: Right — Tasks, Notes, Recommendations (Hidden when in Full Dossier View) -->
    <aside class="panel management-panel" *ngIf="activeWorkspaceTab !== 'profile'">
      <div class="tabs-header">
        <button class="tab-item" (click)="activeWorkspaceTab = 'profile'"><i class="bi bi-person-badge-fill"></i> Client Profile Info</button>
        <button class="tab-item" [class.active]="activeWorkspaceTab === 'tasks'" (click)="activeWorkspaceTab = 'tasks'"><i class="bi bi-check2-square"></i> Tasks ({{ tasks.length }})</button>
        <button class="tab-item" [class.active]="activeWorkspaceTab === 'notes'" (click)="activeWorkspaceTab = 'notes'"><i class="bi bi-journal-text"></i> Notes ({{ notes.length }})</button>
        <button class="tab-item" [class.active]="activeWorkspaceTab === 'timeline'" (click)="activeWorkspaceTab = 'timeline'"><i class="bi bi-clock-history"></i> Timeline</button>
        <button class="tab-item" [class.active]="activeWorkspaceTab === 'recommendations'" (click)="activeWorkspaceTab = 'recommendations'"><i class="bi bi-heart-pulse"></i> Matches ({{ recommendationsHistory.length }})</button>
      </div>
      <div class="panel-body">

        <!-- Tasks -->
        <div *ngIf="activeWorkspaceTab === 'tasks'" class="space-y-4">
          <div class="add-form-box">
            <h5 class="add-form-title">Create Follow-up Task</h5>
            <input type="text" [(ngModel)]="newTaskTitle" placeholder="Task title..." class="form-control text-xs mb-2" />
            <div class="flex items-center gap-2">
              <input type="date" [(ngModel)]="newTaskDueDate" class="form-control text-xs flex-1" />
              <select [(ngModel)]="newTaskPriority" class="form-select text-xs w-24">
                <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option><option value="Urgent">Urgent</option>
              </select>
              <button class="btn-add-item" (click)="createTask()" [disabled]="addingTask || !newTaskTitle.trim()"><i class="bi bi-plus-lg"></i></button>
            </div>
          </div>
          <div class="task-list">
            <div *ngIf="tasks.length === 0" class="empty-list">No tasks yet.</div>
            <div *ngFor="let t of tasks" class="task-item" [class.done]="t.status === 'Completed'">
              <input type="checkbox" [checked]="t.status === 'Completed'" (change)="toggleTaskStatus(t)" />
              <div class="flex-1 min-w-0">
                <span class="task-title">{{ t.title }}</span>
                <div class="task-meta">
                  <span class="priority-badge" [ngClass]="t.priority === 'Urgent' || t.priority === 'High' ? 'urgent' : 'normal'">{{ t.priority }}</span>
                  <span *ngIf="t.due_date" class="due-date"><i class="bi bi-calendar-event"></i> {{ t.due_date | date:'shortDate' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div *ngIf="activeWorkspaceTab === 'notes'" class="space-y-4">
          <div class="add-form-box">
            <h5 class="add-form-title">Add Confidential Note</h5>
            <textarea [(ngModel)]="newNoteText" placeholder="Private notes on preferences..." rows="2" class="form-control text-xs mb-2"></textarea>
            <button class="btn-add-item w-full" (click)="addNote()" [disabled]="addingNote || !newNoteText.trim()"><i class="bi bi-plus-lg"></i> Save Note</button>
          </div>
          <div class="notes-list">
            <div *ngIf="notes.length === 0" class="empty-list">No notes yet.</div>
            <div *ngFor="let n of notes" class="note-item">
              <p>{{ n.note_text }}</p>
              <div class="note-meta"><span>{{ n.rm_name || 'RM' }}</span><span>{{ n.created_at | date:'mediumDate' }}</span></div>
            </div>
          </div>
        </div>

        <!-- Timeline -->
        <div *ngIf="activeWorkspaceTab === 'timeline'" class="space-y-3">
          <div *ngIf="timeline.length === 0" class="empty-list">No events recorded.</div>
          <div *ngFor="let log of timeline" class="timeline-item">
            <div class="timeline-dot"></div>
            <div class="flex-1">
              <span class="timeline-stage">{{ log.stage }}</span>
              <p *ngIf="log.notes" class="timeline-note">{{ log.notes }}</p>
              <div class="timeline-meta"><span>{{ log.updated_by_name }}</span><span>{{ log.created_at | date:'short' }}</span></div>
            </div>
          </div>
        </div>

        <!-- Recommendations -->
        <div *ngIf="activeWorkspaceTab === 'recommendations'" class="space-y-4">
          <div class="flex gap-2 pb-2 border-b border-gray-100">
            <button class="rec-tab" [class.active]="activeRecommendationTab === 'history'" (click)="activeRecommendationTab = 'history'">Shared ({{ recommendationsHistory.length }})</button>
            <button class="rec-tab" [class.active]="activeRecommendationTab === 'bookmarks'" (click)="activeRecommendationTab = 'bookmarks'">Saved ({{ bookmarkedCandidates.length }})</button>
          </div>

          <div *ngIf="activeRecommendationTab === 'history'" class="space-y-2.5">
            <div *ngIf="recommendationsHistory.length === 0" class="empty-list">No profiles shared yet.</div>
            <div *ngFor="let rec of recommendationsHistory" class="rec-item" (click)="openDossier(rec)">
              <div class="rec-avatar"><img *ngIf="rec.photo_url" [src]="rec.photo_url" /><span *ngIf="!rec.photo_url">{{ rec.name?.charAt(0) }}</span></div>
              <div class="rec-info">
                <h6>{{ rec.name }}</h6>
                <p>{{ rec.age }} Yrs • {{ rec.occupation }}</p>
              </div>
              <div class="rec-status">
                <span class="status-badge" [ngClass]="{ 'status-interest': rec.interest_sent, 'status-reject': rec.rejected_by_client, 'status-saved': rec.saved_to_favorites, 'status-viewed': rec.viewed_by_client, 'status-shared': !rec.viewed_by_client && !rec.saved_to_favorites && !rec.interest_sent && !rec.rejected_by_client }">
                  {{ rec.status_label || 'Shared' }}
                </span>
              </div>
            </div>
          </div>

          <div *ngIf="activeRecommendationTab === 'bookmarks'" class="space-y-2.5">
            <div *ngIf="bookmarkedCandidates.length === 0" class="empty-list">No bookmarks yet.</div>
            <div *ngFor="let bkm of bookmarkedCandidates" class="rec-item">
              <div class="rec-avatar"><img *ngIf="bkm.photo_url" [src]="bkm.photo_url" /><span *ngIf="!bkm.photo_url">{{ bkm.name?.charAt(0) }}</span></div>
              <div class="rec-info"><h6>{{ bkm.name }}</h6><p>{{ bkm.age }} Yrs • {{ bkm.occupation }}</p></div>
              <div class="rec-actions">
                <button class="btn-share-bkm" (click)="toggleSelectProfile(bkm); openSearchConsole()"><i class="bi bi-send-plus"></i></button>
                <button class="btn-rm-bkm" (click)="toggleCandidateBookmark(bkm)"><i class="bi bi-bookmark-x"></i></button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </aside>
  </main>

  <!-- ═══════════════════════════════════════════════════════════════════
       FULL-SCREEN MATCHMAKING CONSOLE
  ═══════════════════════════════════════════════════════════════════ -->
  <div class="matchmaking-console" *ngIf="!loading && profile && showSearchConsole">

    <!-- Partner Prefs Banner -->
    <div class="prefs-banner" *ngIf="prefsBannerVisible && !prefsBannerDismissed">
      <div class="banner-content">
        <i class="bi bi-stars text-amber-500"></i>
        <span>Showing profiles based on <strong>{{ getClientFirstName() }}'s</strong> saved Partner Preferences.</span>
      </div>
      <div class="banner-actions">
        <button class="btn-banner-reset" (click)="resetToClientPreferences()"><i class="bi bi-arrow-counterclockwise"></i> Reset Filters</button>
        <button class="btn-banner-edit" (click)="prefsBannerDismissed = true"><i class="bi bi-pencil"></i> Edit Filters</button>
        <button class="btn-banner-dismiss" (click)="prefsBannerDismissed = true; prefsBannerVisible = false"><i class="bi bi-x"></i></button>
      </div>
    </div>

    <!-- Console Body -->
    <div class="console-body" [class.dossier-open]="showDossierDrawer">
      <div class="console-main-panel overflow-y-auto p-5 space-y-5 flex-1 min-w-0">

        <!-- ── Filter Bar (Exact profile-list styling) ─────────────────────────────── -->
        <div class="bg-white rounded-2xl border shadow-sm overflow-visible" style="border-color: rgba(176,30,62,0.12);">
          <!-- Collapsed Toggle Row -->
          <div class="flex flex-wrap items-center gap-3 px-5 py-3.5">
            <!-- Filter Toggle Button -->
            <button (click)="isFilterCollapsed = !isFilterCollapsed"
              class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold border transition-all flex-shrink-0 cursor-pointer"
              [ngStyle]="!isFilterCollapsed
                ? {'background': '#B01E3E', 'color': 'white', 'border-color': '#B01E3E'}
                : {'background': '#fdf0f3', 'color': '#B01E3E', 'border-color': 'rgba(176,30,62,0.2)'}">
              <span class="material-symbols-outlined text-[16px]">tune</span>
              Filters
              <span *ngIf="getActiveFilterCount() > 0"
                class="text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                [ngStyle]="!isFilterCollapsed ? {'background': 'rgba(255,255,255,0.3)', 'color': 'white'} : {'background': '#B01E3E', 'color': 'white'}">
                {{ getActiveFilterCount() }}
              </span>
            </button>

            <!-- Active Filter Chips -->
            <div class="flex flex-wrap gap-1.5 flex-1 items-center">
              <button *ngFor="let chip of getActiveFilterChips()" type="button" (click)="onFilterChipClick(chip)"
                title="Click to jump to this filter section and alter"
                class="inline-flex items-center gap-1 font-medium rounded-full cursor-pointer transition-all border border-transparent hover:border-rose-300 hover:bg-rose-100/70 shadow-2xs"
                style="background: rgba(176,30,62,0.08); color: #B01E3E; font-size: 11.5px; line-height: 16px; padding: 3.5px 10px;">
                <span class="material-symbols-outlined" style="font-size: 12.5px;">edit</span>
                <span>{{ chip.label }}</span>
              </button>
              <span *ngIf="getActiveFilterChips().length === 0" class="text-xs text-gray-400 self-center">
                No filters active — showing all candidates
              </span>
            </div>

            <!-- Reset All -->
            <button *ngIf="getActiveFilterCount() > 0" (click)="resetAllFilters()"
              class="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all bg-transparent flex-shrink-0 cursor-pointer hover:bg-rose-50"
              style="border-color: rgba(176,30,62,0.2); color: #8c4458;">
              Reset All
            </button>
          </div>

          <!-- Expandable Filter Panel -->
          <div [class.hidden]="isFilterCollapsed" class="border-t" style="border-color: rgba(176,30,62,0.08);">
            <!-- Keyword Search Box inside panel -->
            <div class="px-5 pt-3 pb-2 bg-white border-b border-gray-100 flex items-center justify-between gap-4">
              <div class="relative flex-1 max-w-xl">
                <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[18px]">search</span>
                <input type="text" [(ngModel)]="searchKeyword" (keydown.enter)="doSearchProfiles(true)" placeholder="Search profiles by Name, Matrimony ID or keyword..."
                  class="w-full pl-10 pr-24 py-2 text-xs rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-700 shadow-2xs transition-all" />
                <button (click)="doSearchProfiles(true)" class="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg text-white font-semibold text-xs shadow-2xs transition-all cursor-pointer hover:opacity-90" style="background: #B01E3E;">Search</button>
              </div>
            </div>

            <!-- Tab Navigation -->
            <div class="flex overflow-x-auto border-b px-2 pt-1" style="background: #fdf8f9; border-color: rgba(176,30,62,0.08);">
              <button (click)="activeTab = 'basic'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'basic' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'basic' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Basic Criteria
              </button>
              <button (click)="activeTab = 'faith'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'faith' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'faith' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Religion / Caste
              </button>
              <button (click)="activeTab = 'education'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'education' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'education' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Education
              </button>
              <button (click)="activeTab = 'career'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'career' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'career' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Career / Income
              </button>
              <button (click)="activeTab = 'location'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'location' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'location' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Location
              </button>
              <button (click)="activeTab = 'lifestyle'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'lifestyle' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'lifestyle' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Lifestyle
              </button>
              <button (click)="activeTab = 'horoscope'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'horoscope' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'horoscope' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Horoscope
              </button>
              <button (click)="activeTab = 'sort'" class="px-4 py-2.5 text-xs whitespace-nowrap transition-colors cursor-pointer"
                [ngClass]="activeTab === 'sort' ? 'border-b-[3px] font-bold' : 'border-transparent font-medium text-gray-400'"
                [ngStyle]="activeTab === 'sort' ? {'border-color': '#B01E3E', 'color': '#B01E3E'} : {}">
                Sorting & Options
              </button>
            </div>

            <!-- Filter Fields -->
            <form [formGroup]="formGroup" (submit)="$event.preventDefault(); applyFilter()">
              <div class="p-5 bg-white">
                <!-- Basic Criteria -->
                <div *ngIf="activeTab === 'basic'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-4 pb-2">
                  <!-- Marital Status -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Marital Status</label>
                    <button type="button" (click)="toggleFilterDropdown('marital', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.marital ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('marital', 'Any Status') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'marital'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'marital'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['marital'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('marital', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.marital ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Status</span><span *ngIf="!formGroup.value.marital"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div
                        *ngFor="let status of ['Never Married', 'Widowed', 'Divorced']"
                        (click)="setSingleSelect('marital', status)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="formGroup.value.marital === status ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>{{ status }}</span><span *ngIf="formGroup.value.marital === status"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>

                  <!-- Age Range -->
                  <div>
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider" style="color: #8c4458;">Age
                      Range</label>
                    <div class="flex items-center gap-1.5">
                      <div class="flex-1 relative searchable-multi-select-container">
                        <button type="button" (click)="toggleFilterDropdown('agefrom', $event)"
                          class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-2.5 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                          <span class="truncate block pr-1"
                            [ngClass]="formGroup.value.agefrom ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                            getSingleSelectLabel('agefrom', 'Min') }}</span>
                          <span class="material-symbols-outlined text-gray-500 text-[14px] flex-shrink-0">expand_more</span>
                        </button>
                        <div *ngIf="activeDropdown === 'agefrom'"
                          class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                          [ngClass]="dropdownDirection['agefrom'] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'">
                          <div (click)="setSingleSelect('agefrom', '')"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="!formGroup.value.agefrom ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            Min</div>
                          <div *ngFor="let age of ageFromValues" (click)="setSingleSelect('agefrom', age)"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="formGroup.value.agefrom == age ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            {{ age }} yrs</div>
                        </div>
                      </div>
                      <span class="text-gray-400 text-xs flex-shrink-0">–</span>
                      <div class="flex-1 relative searchable-multi-select-container">
                        <button type="button" (click)="toggleFilterDropdown('ageto', $event)"
                          class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-2.5 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                          <span class="truncate block pr-1"
                            [ngClass]="formGroup.value.ageto ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                            getSingleSelectLabel('ageto', 'Max') }}</span>
                          <span class="material-symbols-outlined text-gray-500 text-[14px] flex-shrink-0">expand_more</span>
                        </button>
                        <div *ngIf="activeDropdown === 'ageto'"
                          class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                          [ngClass]="dropdownDirection['ageto'] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'">
                          <div (click)="setSingleSelect('ageto', '')"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="!formGroup.value.ageto ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            Max</div>
                          <div *ngFor="let age of ageToValues" (click)="setSingleSelect('ageto', age)"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="formGroup.value.ageto == age ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            {{ age }} yrs</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Height Range -->
                  <div>
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Height Range</label>
                    <div class="flex items-center gap-1.5">
                      <div class="flex-1 relative searchable-multi-select-container">
                        <button type="button" (click)="toggleFilterDropdown('heightfrom', $event)"
                          class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-2.5 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                          <span class="truncate block pr-1"
                            [ngClass]="formGroup.value.heightfrom ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                            getSingleSelectLabel('heightfrom', 'Min') }}</span>
                          <span class="material-symbols-outlined text-gray-500 text-[14px] flex-shrink-0">expand_more</span>
                        </button>
                        <div *ngIf="activeDropdown === 'heightfrom'"
                          class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                          [ngClass]="dropdownDirection['heightfrom'] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'">
                          <div (click)="setSingleSelect('heightfrom', '')"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="!formGroup.value.heightfrom ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            Min</div>
                          <div *ngFor="let h of heightFromValues" (click)="setSingleSelect('heightfrom', h)"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="formGroup.value.heightfrom == h ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            {{ h }} cm</div>
                        </div>
                      </div>
                      <span class="text-gray-400 text-xs flex-shrink-0">–</span>
                      <div class="flex-1 relative searchable-multi-select-container">
                        <button type="button" (click)="toggleFilterDropdown('heightto', $event)"
                          class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-2.5 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                          <span class="truncate block pr-1"
                            [ngClass]="formGroup.value.heightto ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                            getSingleSelectLabel('heightto', 'Max') }}</span>
                          <span class="material-symbols-outlined text-gray-500 text-[14px] flex-shrink-0">expand_more</span>
                        </button>
                        <div *ngIf="activeDropdown === 'heightto'"
                          class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                          [ngClass]="dropdownDirection['heightto'] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'">
                          <div (click)="setSingleSelect('heightto', '')"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="!formGroup.value.heightto ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            Max</div>
                          <div *ngFor="let h of heightToValues" (click)="setSingleSelect('heightto', h)"
                            class="px-2.5 py-1.5 rounded-lg text-xs cursor-pointer"
                            [ngClass]="formGroup.value.heightto == h ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                            {{ h }} cm</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Physical Status -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Physical Status</label>
                    <button type="button" (click)="toggleFilterDropdown('physical', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.physical ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('physical', 'Any Status') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'physical'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'physical'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['physical'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('physical', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.physical ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Status</span><span *ngIf="!formGroup.value.physical"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div *ngFor="let p of ['Normal', 'Physically Challenged']" (click)="setSingleSelect('physical', p)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="formGroup.value.physical === p ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>{{ p }}</span><span *ngIf="formGroup.value.physical === p"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>

                  <!-- Family Type -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider" style="color: #8c4458;">Family
                      Type</label>
                    <button type="button" (click)="toggleFilterDropdown('familytype', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.familytype ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('familytype', 'Any Type') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'familytype'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'familytype'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['familytype'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('familytype', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.familytype ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Type</span><span *ngIf="!formGroup.value.familytype"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div *ngFor="let ft of ['Joint', 'Nuclear', 'Extended']"
                        (click)="setSingleSelect('familytype', ft)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="formGroup.value.familytype === ft ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>{{ ft }}</span><span *ngIf="formGroup.value.familytype === ft"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Religion / Caste -->
                <div *ngIf="activeTab === 'faith'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Religion -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Religion</label>
                    <button type="button" (click)="toggleFilterDropdown('religion', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.religion && formGroup.value.religion.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('religion', 'Any Religion', religions, 'religion_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'religion'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'religion'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['religion'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search religion..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('religion')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.religion || formGroup.value.religion.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Religion</span><span *ngIf="!formGroup.value.religion || formGroup.value.religion.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let rel of religions">
                        <div
                          *ngIf="!singleSearchQuery || rel.religion_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('religion', rel._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('religion', rel._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('religion', rel._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ rel.religion_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Caste -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Caste</label>
                    <button type="button" (click)="toggleFilterDropdown('caste', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.caste && formGroup.value.caste.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('caste', 'Any Caste', castes, 'caste_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'caste'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'caste'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['caste'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search caste..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('caste')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.caste || formGroup.value.caste.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Caste</span><span *ngIf="!formGroup.value.caste || formGroup.value.caste.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let caste of castes">
                        <div
                          *ngIf="!singleSearchQuery || caste.caste_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('caste', caste._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('caste', caste._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('caste', caste._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ caste.caste_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Sub-Caste -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Sub-Caste</label>
                    <button type="button" (click)="toggleFilterDropdown('subcaste', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.subcaste && formGroup.value.subcaste.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('subcaste', 'Any Sub-Caste', subcastes, 'subcaste_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'subcaste'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'subcaste'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['subcaste'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search sub-caste..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('subcaste')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.subcaste || formGroup.value.subcaste.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Sub-Caste</span><span *ngIf="!formGroup.value.subcaste || formGroup.value.subcaste.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let sub of subcastes">
                        <div
                          *ngIf="!singleSearchQuery || sub.subcaste_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('subcaste', sub._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('subcaste', sub._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('subcaste', sub._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ sub.subcaste_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Mother Tongue -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Mother Tongue</label>
                    <button type="button" (click)="toggleFilterDropdown('language', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.language && formGroup.value.language.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('language', 'Any Language', languages, 'language_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'language'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'language'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['language'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search language..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('language')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.language || formGroup.value.language.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Language</span><span *ngIf="!formGroup.value.language || formGroup.value.language.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let lang of languages">
                        <div
                          *ngIf="!singleSearchQuery || lang.language_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('language', lang._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('language', lang._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('language', lang._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ lang.language_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>
                <!-- Education -->
                <div *ngIf="activeTab === 'education'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Education Category -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Education Category</label>
                    <button type="button" (click)="toggleFilterDropdown('educationcategory', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.educationcategory && formGroup.value.educationcategory.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('educationcategory', 'Any Category', educationCategories,
                        'educationcategory_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'educationcategory'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'educationcategory'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['educationcategory'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search category..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('educationcategory')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.educationcategory || formGroup.value.educationcategory.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Category</span><span
                          *ngIf="!formGroup.value.educationcategory || formGroup.value.educationcategory.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let cat of educationCategories">
                        <div
                          *ngIf="!singleSearchQuery || cat.educationcategory_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('educationcategory', cat._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('educationcategory', cat._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('educationcategory', cat._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ cat.educationcategory_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Education Details -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Education Details</label>
                    <button type="button" (click)="toggleFilterDropdown('educationdetails', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.educationdetails && formGroup.value.educationdetails.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('educationdetails', 'Any Degree', educationDetailsList,
                        'educationdetails_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'educationdetails'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'educationdetails'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['educationdetails'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search degree..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('educationdetails')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.educationdetails || formGroup.value.educationdetails.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Degree</span><span
                          *ngIf="!formGroup.value.educationdetails || formGroup.value.educationdetails.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let deg of educationDetailsList">
                        <div
                          *ngIf="!singleSearchQuery || deg.educationdetails_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('educationdetails', deg._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('educationdetails', deg._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('educationdetails', deg._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ deg.educationdetails_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>

                <!-- Career & Income -->
                <div *ngIf="activeTab === 'career'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Occupation -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Occupation</label>
                    <button type="button" (click)="toggleFilterDropdown('occupation', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.occupation && formGroup.value.occupation.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('occupation', 'Any Occupation', occupations, 'occupation_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'occupation'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'occupation'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['occupation'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search occupation..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('occupation')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.occupation || formGroup.value.occupation.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Occupation</span><span *ngIf="!formGroup.value.occupation || formGroup.value.occupation.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let occ of occupations">
                        <div
                          *ngIf="!singleSearchQuery || occ.occupation_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('occupation', occ._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('occupation', occ._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('occupation', occ._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ occ.occupation_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Employed In -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Employed In</label>
                    <button type="button" (click)="toggleFilterDropdown('employedin', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.employedin && formGroup.value.employedin.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('employedin', 'Any Sector', employmentTypes, 'employmenttype_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'employedin'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'employedin'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['employedin'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search sector..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('employedin')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.employedin || formGroup.value.employedin.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Sector</span><span *ngIf="!formGroup.value.employedin || formGroup.value.employedin.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let emp of employmentTypes">
                        <div
                          *ngIf="!singleSearchQuery || emp.employmenttype_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('employedin', emp._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('employedin', emp._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('employedin', emp._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ emp.employmenttype_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Annual Income Range -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider" style="color: #8c4458;">Annual
                      Income</label>
                    <button type="button" (click)="toggleFilterDropdown('income', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.income && formGroup.value.income.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('income', 'Any Income Range', incomes, 'income_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'income'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'income'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['income'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search income..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('income')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.income || formGroup.value.income.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Income Range</span><span *ngIf="!formGroup.value.income || formGroup.value.income.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let inc of incomes">
                        <div
                          *ngIf="!singleSearchQuery || inc.income_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('income', inc._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('income', inc._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('income', inc._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ inc.income_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>
                <!-- Location -->
                <div *ngIf="activeTab === 'location'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Country -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Country</label>
                    <button type="button" (click)="toggleFilterDropdown('country', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.country && formGroup.value.country.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('country', 'Any Country', countries, 'country_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'country'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'country'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['country'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search country..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('country')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.country || formGroup.value.country.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Country</span><span *ngIf="!formGroup.value.country || formGroup.value.country.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let cntry of countries">
                        <div
                          *ngIf="!singleSearchQuery || cntry.country_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('country', cntry._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('country', cntry._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('country', cntry._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ cntry.country_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- State -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">State</label>
                    <button type="button" (click)="toggleFilterDropdown('state', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.state && formGroup.value.state.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('state', 'Any State', states, 'state_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'state'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'state'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['state'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search state..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('state')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.state || formGroup.value.state.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any State</span><span *ngIf="!formGroup.value.state || formGroup.value.state.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let st of states">
                        <div
                          *ngIf="!singleSearchQuery || st.state_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('state', st._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('state', st._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('state', st._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ st.state_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- District / City -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">District / City</label>
                    <button type="button" (click)="toggleFilterDropdown('district', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.district && formGroup.value.district.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('district', 'Any District', districts, 'district_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'district'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'district'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['district'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search district..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('district')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.district || formGroup.value.district.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any District</span><span
                          *ngIf="!formGroup.value.district || formGroup.value.district.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let dist of districts">
                        <div
                          *ngIf="!singleSearchQuery || dist.district_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('district', dist._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('district', dist._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('district', dist._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ dist.district_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>

                <!-- Lifestyle -->
                <div *ngIf="activeTab === 'lifestyle'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Eating Habits -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Diet / Eating</label>
                    <button type="button" (click)="toggleFilterDropdown('eating', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.eating ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('eating', 'Any Diet') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'eating'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'eating'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['eating'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('eating', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.eating ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Diet</span><span *ngIf="!formGroup.value.eating"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div
                        *ngFor="let diet of ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Jain / Vegan']"
                        (click)="setSingleSelect('eating', diet)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="formGroup.value.eating === diet ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>{{ diet }}</span><span *ngIf="formGroup.value.eating === diet"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>

                  <!-- Drinking Habits -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Drinking Habits</label>
                    <button type="button" (click)="toggleFilterDropdown('drinking', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.drinking ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('drinking', 'Any Habit') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'drinking'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'drinking'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['drinking'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('drinking', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.drinking ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Habit</span><span *ngIf="!formGroup.value.drinking"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div *ngFor="let habit of ['Non-Drinker', 'Occasional Drinker', 'Regular Drinker']"
                        (click)="setSingleSelect('drinking', habit)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="formGroup.value.drinking === habit ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>{{ habit }}</span><span *ngIf="formGroup.value.drinking === habit"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>

                  <!-- Smoking Habits -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Smoking Habits</label>
                    <button type="button" (click)="toggleFilterDropdown('smoking', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate pr-1 block"
                        [ngClass]="formGroup.value.smoking ? 'font-semibold text-rose-950' : 'text-gray-500'">
                        {{ getSingleSelectLabel('smoking', 'Any Habit') }}
                      </span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'smoking'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'smoking'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto transition-all"
                      [ngClass]="dropdownDirection['smoking'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div (click)="setSingleSelect('smoking', '')"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="!formGroup.value.smoking ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>Any Habit</span><span *ngIf="!formGroup.value.smoking"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <div *ngFor="let habit of ['Non-Smoker', 'Occasional Smoker', 'Regular Smoker']"
                        (click)="setSingleSelect('smoking', habit)"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer transition-colors flex items-center justify-between"
                        [ngClass]="formGroup.value.smoking === habit ? 'bg-rose-100/80 font-bold text-rose-950' : 'text-gray-700 hover:bg-rose-50'">
                        <span>{{ habit }}</span><span *ngIf="formGroup.value.smoking === habit"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Horoscope -->
                <div *ngIf="activeTab === 'horoscope'"
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4 pb-2">
                  <!-- Star -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Star (Nakshatra)</label>
                    <button type="button" (click)="toggleFilterDropdown('star', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.star && formGroup.value.star.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('star', 'Any Star', stars, 'star_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'star'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'star'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['star'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search star..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('star')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.star || formGroup.value.star.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Star</span><span *ngIf="!formGroup.value.star || formGroup.value.star.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let st of stars">
                        <div
                          *ngIf="!singleSearchQuery || st.star_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('star', st._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('star', st._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('star', st._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ st.star_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Raasi -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Raasi (Moon Sign)</label>
                    <button type="button" (click)="toggleFilterDropdown('raasi', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.raasi && formGroup.value.raasi.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('raasi', 'Any Raasi', raasis, 'raasi_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'raasi'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'raasi'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['raasi'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search raasi..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('raasi')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.raasi || formGroup.value.raasi.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Raasi</span><span *ngIf="!formGroup.value.raasi || formGroup.value.raasi.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let r of raasis">
                        <div
                          *ngIf="!singleSearchQuery || r.raasi_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('raasi', r._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('raasi', r._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('raasi', r._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ r.raasi_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>

                  <!-- Dosham -->
                  <div class="relative searchable-multi-select-container">
                    <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                      style="color: #8c4458;">Dosham</label>
                    <button type="button" (click)="toggleFilterDropdown('dosham', $event)"
                      class="w-full flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none transition-all cursor-pointer text-left shadow-sm">
                      <span class="truncate block pr-1"
                        [ngClass]="(formGroup.value.dosham && formGroup.value.dosham.length > 0) ? 'font-semibold text-rose-950' : 'text-gray-500'">{{
                        getMultiSelectLabel('dosham', 'Any Dosham Status', doshams, 'dosham_nme') }}</span>
                      <span
                        class="material-symbols-outlined text-gray-500 text-[16px] transition-transform duration-200 flex-shrink-0"
                        [class.rotate-180]="activeDropdown === 'dosham'">expand_more</span>
                    </button>
                    <div *ngIf="activeDropdown === 'dosham'"
                      class="absolute z-[999] left-0 right-0 bg-white rounded-2xl border border-gray-200 shadow-2xl p-1.5 max-h-[220px] overflow-y-auto"
                      [ngClass]="dropdownDirection['dosham'] === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'">
                      <div class="sticky top-0 bg-white z-10 pb-1.5 mb-1 border-b border-gray-100">
                        <input type="text" [(ngModel)]="singleSearchQuery" [ngModelOptions]="{standalone: true}"
                          placeholder="Search dosham..."
                          class="w-full px-2.5 py-1 rounded-lg border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-rose-400">
                      </div>
                      <div (click)="formGroup.get('dosham')?.setValue([])"
                        class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                        [ngClass]="(!formGroup.value.dosham || formGroup.value.dosham.length === 0) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                        <span>Any Dosham Status</span><span
                          *ngIf="!formGroup.value.dosham || formGroup.value.dosham.length === 0"
                          class="material-symbols-outlined text-[14px] text-rose-700">check</span>
                      </div>
                      <ng-container *ngFor="let d of doshams">
                        <div
                          *ngIf="!singleSearchQuery || d.dosham_nme?.toLowerCase()?.includes(singleSearchQuery.toLowerCase())"
                          (click)="toggleMultiSelect('dosham', d._id)"
                          class="px-3 py-2 rounded-xl text-xs cursor-pointer flex items-center justify-between"
                          [ngClass]="isMultiSelected('dosham', d._id) ? 'bg-rose-100/80 font-bold text-rose-950' : 'hover:bg-rose-50 text-gray-700'">
                          <div class="flex items-center gap-2">
                            <input type="checkbox" [checked]="isMultiSelected('dosham', d._id)" class="rounded text-rose-600 focus:ring-rose-500 pointer-events-none" style="accent-color: #B01E3E;">
                            <span>{{ d.dosham_nme }}</span>
                          </div>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>

                <!-- Sorting & Options -->
                <div *ngIf="activeTab === 'sort'" class="space-y-4 pb-2">
                  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                    <div>
                      <label class="block text-[10px] font-bold mb-1.5 uppercase tracking-wider"
                        style="color: #8c4458;">Sort Order</label>
                      <select formControlName="sort_by" (change)="applyFilter()"
                        class="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs bg-white hover:border-gray-300 focus:outline-none focus:border-rose-400 font-medium text-gray-700 shadow-sm cursor-pointer">
                        <option value="compatibility">Sort by: Compatibility Score (High to Low)</option>
                        <option value="recent">Sort by: Recently Created</option>
                        <option value="last_login">Sort by: Last Active</option>
                      </select>
                    </div>
                  </div>

                  <div class="pt-2 border-t border-gray-100">
                    <label class="block text-[10px] font-bold mb-2 uppercase tracking-wider"
                      style="color: #8c4458;">Quick Toggles</label>
                    <div class="flex flex-wrap items-center gap-6">
                      <label
                        class="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" formControlName="verified_only" (change)="applyFilter()"
                          class="rounded text-rose-600 focus:ring-rose-500 w-4 h-4 cursor-pointer" style="accent-color: #B01E3E;" />
                        <span>Verified Profiles Only</span>
                      </label>
                      <label
                        class="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" formControlName="with_photo_only" (change)="applyFilter()"
                          class="rounded text-rose-600 focus:ring-rose-500 w-4 h-4 cursor-pointer" style="accent-color: #B01E3E;" />
                        <span>Profiles With Photo Only</span>
                      </label>
                      <label
                        class="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" formControlName="recently_active_only" (change)="applyFilter()"
                          class="rounded text-rose-600 focus:ring-rose-500 w-4 h-4 cursor-pointer" style="accent-color: #B01E3E;" />
                        <span>Recently Active Only</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filter Action Bar -->
              <div class="flex items-center justify-between px-5 py-3 border-t" style="border-color: rgba(176,30,62,0.08); background: #fdf8f9;">
                <span class="text-xs font-semibold flex items-center gap-1.5" style="color: #8c4458;">
                  <span class="material-symbols-outlined text-[15px]">tune</span>
                  {{ getActiveFilterCount() }} Active Filter{{ getActiveFilterCount() !== 1 ? 's' : '' }}
                </span>
                <div class="flex items-center gap-2.5">
                  <button type="button" (click)="resetAllFilters()" class="px-5 py-2 text-white rounded-xl text-xs font-bold shadow-sm transition-all border-0 flex items-center gap-1.5 cursor-pointer hover:opacity-90" style="background: #B01E3E;">
                    <span class="material-symbols-outlined text-[16px]">restart_alt</span>
                    Reset
                  </button>
                  <button type="button" (click)="applyFilter()" class="px-6 py-2 text-white rounded-xl text-xs font-bold shadow-sm transition-all border-0 flex items-center gap-1.5 cursor-pointer hover:opacity-90" style="background: #B01E3E;">
                    <span class="material-symbols-outlined text-[16px]">search</span>
                    Apply Filters
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- ── Profile Grid (Exact profile-list component structure) ─────────────────── -->
        <div class="profile-grid relative">
          <div *ngFor="let p of searchedProfiles" class="pcard relative transition-all"
            [ngStyle]="isProfileSelected(p) ? {'border': '2px solid #10b981', 'box-shadow': '0 8px 24px rgba(16,185,129,0.18)'} : {}">

            <!-- Image Area -->
            <div class="pcard-img" (click)="openDossier(p, $event)">
              <!-- Top-Left Compatibility Match Badge -->
              <div class="pcard-match-badge badge-strong" [ngStyle]="{'background': getScoreColor(p.compatibility_score || 85)}">
                <span class="material-symbols-outlined text-[11px]">stars</span>
                {{ p.compatibility_score || 85 }}% Match
              </div>

              <!-- Top-Right Selection Checkbox / Badge -->
              <button type="button" (click)="$event.stopPropagation(); toggleSelectProfile(p)"
                class="pcard-fav-btn" [ngStyle]="isProfileSelected(p) ? {'background': '#10b981', 'border-color': '#10b981'} : {}" title="Select to Share">
                <span class="material-symbols-outlined text-[15px]">{{ isProfileSelected(p) ? 'check' : 'add' }}</span>
              </button>

              <!-- Candidate Photo or Placeholder -->
              <ng-container *ngIf="base64Images[p._id] || p.photo_url || (p.gallery && p.gallery.length > 0); else defaultCardAvatar">
                <img [src]="base64Images[p._id] ? ('data:image/jpeg;base64,' + base64Images[p._id]) : (p.photo_url || p.gallery[0])" alt="Candidate photo" />
              </ng-container>
              <ng-template #defaultCardAvatar>
                <div class="pcard-avatar-placeholder">
                  <span class="material-symbols-outlined text-[72px]" style="color: rgba(176,30,62,0.15);">person</span>
                </div>
              </ng-template>

              <!-- Gradient -->
              <div class="pcard-gradient"></div>

              <!-- Name & Metadata Overlay -->
              <div class="pcard-overlay">
                <div class="flex items-center gap-1.5 mb-0.5">
                  <h3 class="font-display text-[15px] font-bold m-0 truncate text-white drop-shadow-sm leading-tight">
                    {{ p.name }}
                  </h3>
                  <span *ngIf="p.verification_status === 'VERIFIED' || p.verified" class="material-symbols-outlined fill-icon text-[14px] flex-shrink-0 drop-shadow text-white" title="Verified Member">verified</span>
                </div>
                <div class="flex items-center gap-1.5 text-white/85 text-[11px] font-normal drop-shadow-sm truncate">
                  <span>{{ p.age }} yrs</span>
                  <span class="opacity-40">·</span>
                  <span class="truncate">{{ p.city || p.state || p.location || 'India' }}</span>
                  <ng-container *ngIf="p.horoscope || p.rasi || p.nakshatra">
                    <span class="opacity-40">·</span>
                    <span class="flex items-center gap-0.5 text-white flex-shrink-0 font-medium" title="Horoscope Available">
                      <span class="material-symbols-outlined text-[11px]">auto_awesome</span>Horoscope
                    </span>
                  </ng-container>
                </div>
              </div>
            </div>

            <!-- Info Chips -->
            <div class="info-chips" (click)="openDossier(p, $event)">
              <span class="info-chip" title="Religion / Caste">
                <span class="material-symbols-outlined text-[12px]">temple_hindu</span>
                {{ p.religion || 'Hindu' }}{{ p.caste ? ' · ' + p.caste : '' }}
              </span>
              <span class="info-chip" title="Education">
                <span class="material-symbols-outlined text-[12px]">school</span>
                {{ p.education || 'Degree' }}
              </span>
              <span class="info-chip" title="Occupation">
                <span class="material-symbols-outlined text-[12px]">work</span>
                {{ p.occupation || 'Professional' }}
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="pcard-actions">
              <button type="button" (click)="$event.stopPropagation(); openDossier(p, $event)" class="btn-view">
                <span class="material-symbols-outlined text-[14px] mr-1">person_search</span> Inspect
              </button>
              <button type="button" (click)="$event.stopPropagation(); toggleSelectProfile(p)" class="btn-connect btn-ripple"
                [ngStyle]="isProfileSelected(p) ? {'background': '#10b981', 'color': 'white'} : {}">
                <span class="material-symbols-outlined text-[14px] mr-1">{{ isProfileSelected(p) ? 'check_circle' : 'add_circle' }}</span>
                {{ isProfileSelected(p) ? 'Selected' : 'Select' }}
              </button>
            </div>
          </div>
        </div>

        <!-- No Results State -->
        <div *ngIf="!searchingProfiles && searchedProfiles.length === 0" class="bg-white rounded-[20px] p-14 text-center border border-[#EBEBEB] shadow-sm max-w-sm mx-auto mt-6">
          <div class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style="background: #FFF0F4;">
            <span class="material-symbols-outlined text-3xl" style="color: #B01E3E;">search_off</span>
          </div>
          <h3 class="font-display font-bold text-lg text-gray-800 m-0">No Profiles Found</h3>
          <p class="text-gray-400 text-xs leading-relaxed mt-2 mb-5">
            Try relaxing your filters or resetting them to discover more candidates.
          </p>
          <button (click)="resetAllFilters()" class="btn-connect btn-ripple px-6 border-0 cursor-pointer" style="flex: unset; padding: 10px 22px; border-radius: 12px;">
            Reset All Filters
          </button>
        </div>

        <!-- Load More Spinner / Button -->
        <div *ngIf="hasMoreProfiles" class="flex justify-center py-6">
          <button (click)="loadMoreProfiles()" [disabled]="searchingProfiles" class="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold text-xs rounded-xl hover:bg-gray-50 transition-all shadow-2xs flex items-center gap-2">
            <span *ngIf="!searchingProfiles"><i class="bi bi-chevron-down"></i> Load More Candidates</span>
            <span *ngIf="searchingProfiles" class="spinner-border spinner-border-sm text-primary"></span>
          </button>
        </div>

        <!-- Sticky Share Bar -->
        <div class="sticky-share-bar" *ngIf="selectedProfilesForShare.length > 0">
          <div class="share-bar-left">
            <span><i class="bi bi-check-circle-fill text-emerald-400"></i> <strong>{{ selectedProfilesForShare.length }}</strong> candidate{{ selectedProfilesForShare.length > 1 ? 's' : '' }} selected</span>
          </div>
          <div class="share-bar-center" *ngIf="showRmNoteInput">
            <textarea [(ngModel)]="rmNoteForShare" placeholder="Optional: Add your RM recommendation note... e.g. 'I shortlisted this profile because education, family values and location closely match your expectations.'" rows="2" class="rm-note-textarea"></textarea>
          </div>
          <div class="share-bar-right">
            <button class="btn-toggle-note" (click)="showRmNoteInput = !showRmNoteInput">
              <i class="bi bi-pencil-square"></i> {{ showRmNoteInput ? 'Hide Note' : 'Add RM Note' }}
            </button>
            <button class="btn-clear-selection" (click)="selectedProfilesForShare = []">
              <i class="bi bi-x"></i> Clear
            </button>
            <button class="btn-confirm-share" (click)="confirmShareProfiles()" [disabled]="sendingChat">
              <i class="bi" [ngClass]="sendingChat ? 'bi-hourglass-split' : 'bi-send-fill'"></i>
              {{ sendingChat ? 'Sharing...' : 'Share Into Client Chat' }}
            </button>
          </div>
        </div>

      </div><!-- /profiles-grid-area -->

      <!-- ─── RIGHT: DOSSIER SIDE DRAWER (Exact Profile Details Component UI Replica) ─── -->
      <div class="dossier-drawer" *ngIf="showDossierDrawer" (click)="$event.stopPropagation()">
        <div class="dossier-header">
          <div class="dossier-title">
            <h5 class="flex items-center gap-2 m-0">
              <span>{{ dossierCandidate?.name }}</span>
              <span *ngIf="dossierCandidate?.verification_status === 'VERIFIED' || dossierCandidate?.verification_status === 'Verified'" class="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200/50 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">
                <i class="bi bi-patch-check-fill text-[11px]"></i> Verified
              </span>
            </h5>
            <p class="m-0 mt-0.5 text-gray-500 font-mono text-xs">ID: #{{ (dossierCandidate?._id || dossierCandidate?.profile_id)?.toString()?.slice(-6)?.toUpperCase() }} · {{ dossierCandidate?.last_seen_label || 'Recently Active' }}</p>
          </div>
          <div class="dossier-header-actions">
            <span class="dossier-score" [style.color]="getScoreColor(dossierCandidate?.compatibility_score || 85)">
              {{ dossierCandidate?.compatibility_score || 85 }}% Match
            </span>
            <button class="btn-close-dossier" (click)="closeDossier()" title="Close Inspection"><i class="bi bi-x-lg"></i></button>
          </div>
        </div>

        <div *ngIf="loadingDossier" class="dossier-loading py-16 text-center">
          <div class="spinner-border text-danger mb-2"></div>
          <p class="text-xs text-gray-500 font-medium">Loading complete profile & inspection records...</p>
        </div>

        <div *ngIf="!loadingDossier && dossierCandidate" class="dossier-body pd-main-wrapper p-4">

          <!-- 1. COMPACT HERO SECTION -->
          <div class="pd-hero-card">
            <div class="pd-hero-grid">
              
              <!-- LEFT: Photo & Gallery (~240px wide) -->
              <div class="flex flex-col">
                <div class="pd-main-photo group relative rounded-2xl overflow-hidden bg-slate-50">
                  <ng-container *ngIf="dossierGalleryBase64[galleryActiveIndex] || (dossierCandidate._id && base64Images[dossierCandidate._id]) || (dossierCandidate.gallery && dossierCandidate.gallery[galleryActiveIndex]) || dossierCandidate.photo_url; else defaultDossierAvatar">
                    <img [src]="dossierGalleryBase64[galleryActiveIndex] ? ('data:image/jpeg;base64,' + dossierGalleryBase64[galleryActiveIndex]) : ((dossierCandidate._id && base64Images[dossierCandidate._id] && galleryActiveIndex === 0) ? ('data:image/jpeg;base64,' + base64Images[dossierCandidate._id]) : ((dossierCandidate.gallery && dossierCandidate.gallery[galleryActiveIndex]) || dossierCandidate.photo_url))" class="w-full h-full object-contain transition-all duration-300" alt="Candidate photo" />
                  </ng-container>
                  <ng-template #defaultDossierAvatar>
                    <div class="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-slate-300">
                      <i class="bi bi-person-fill text-[64px]"></i>
                    </div>
                  </ng-template>
                </div>
                
                <!-- Thumbnail Strip (Main DP + Gallery slots) -->
                <div class="pd-thumb-strip scrollbar-none flex gap-1.5 mt-2 overflow-x-auto pb-1" *ngIf="dossierCandidate.gallery && dossierCandidate.gallery.length > 1">
                  <div *ngFor="let photo of dossierCandidate.gallery; let i = index" (click)="galleryActiveIndex = i" class="pd-thumb border-2 cursor-pointer flex-shrink-0 aspect-square rounded-lg overflow-hidden relative bg-slate-50" [class.border-primary]="i === galleryActiveIndex" [class.border-transparent]="i !== galleryActiveIndex">
                    <img [src]="dossierGalleryBase64[i] ? ('data:image/jpeg;base64,' + dossierGalleryBase64[i]) : photo" class="w-full h-full object-cover transition-transform hover:scale-105" />
                  </div>
                </div>
              </div>

              <!-- RIGHT: Summary & Quick Overview -->
              <div class="pd-hero-right flex flex-col gap-3">
                <div class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div>
                    <h2 class="font-display font-bold text-xl text-primary m-0">{{ dossierCandidate.name }}</h2>
                    <div class="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                      <span>{{ dossierCandidate.age }} Yrs · {{ dossierCandidate.gender || 'Member' }}</span>
                      <span>•</span>
                      <span class="text-emerald-600 font-semibold flex items-center gap-1">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> {{ dossierCandidate.verification_status || 'VERIFIED' }}
                      </span>
                    </div>
                  </div>

                  <!-- Action Row -->
                  <div class="flex items-center gap-2">
                    <button (click)="toggleCandidateBookmark(dossierCandidate)" class="h-8 px-3 rounded-lg border border-primary/20 flex items-center gap-1.5 text-xs font-bold transition-colors cursor-pointer" [ngClass]="dossierCandidate.is_bookmarked ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white text-primary hover:bg-primary/5'">
                      <i class="bi" [ngClass]="dossierCandidate.is_bookmarked ? 'bi-bookmark-star-fill text-amber-600' : 'bi-bookmark-star'"></i>
                      {{ dossierCandidate.is_bookmarked ? 'Bookmarked' : 'Bookmark' }}
                    </button>
                    <button (click)="toggleSelectProfile(dossierCandidate)" class="h-8 px-3.5 rounded-lg text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 border-0 cursor-pointer text-white" [ngClass]="isProfileSelected(dossierCandidate) ? 'bg-red-700 hover:bg-red-800' : 'bg-primary hover:bg-primary/90'">
                      <i class="bi" [ngClass]="isProfileSelected(dossierCandidate) ? 'bi-check-circle-fill' : 'bi-plus-circle-fill'"></i>
                      {{ isProfileSelected(dossierCandidate) ? 'Remove from Share' : 'Select & Share' }}
                    </button>
                  </div>
                </div>

                <!-- Compact Chips Attributes -->
                <div class="pd-chip-list flex flex-wrap gap-2">
                  <span class="pd-chip">
                    <i class="bi bi-person-fill text-primary"></i>
                    {{ dossierCandidate.age }} Yrs • {{ dossierCandidate.height || '--' }}
                  </span>
                  <span class="pd-chip">
                    <i class="bi bi-building text-primary"></i>
                    {{ dossierCandidate.religion || '--' }}, {{ dossierCandidate.caste || '--' }}
                  </span>
                  <span class="pd-chip">
                    <i class="bi bi-briefcase-fill text-primary"></i>
                    {{ dossierCandidate.occupation || 'Professional' }}
                  </span>
                  <span class="pd-chip">
                    <i class="bi bi-geo-alt-fill text-primary"></i>
                    {{ dossierCandidate.location || 'India' }}
                  </span>
                  <span class="pd-chip">
                    <i class="bi bi-mortarboard-fill text-primary"></i>
                    {{ dossierCandidate.education || 'Degree' }}
                  </span>
                </div>

                <!-- About Candidate Summary -->
                <div *ngIf="dossierCandidate.about" class="mt-1">
                  <h3 class="font-display font-bold text-[10px] uppercase tracking-wider text-slate-400 mb-1 m-0">About Candidate</h3>
                  <p class="text-xs text-slate-700 leading-relaxed font-serif m-0">{{ dossierCandidate.about }}</p>
                </div>

                <!-- Safe Matrimony / Verified Matchmaker minimal notice -->
                <div class="bg-amber-50/80 rounded-lg px-3 py-1.5 border border-amber-200/60 flex items-center gap-2 text-amber-900 text-[11px] font-medium mt-auto">
                  <i class="bi bi-shield-check text-[15px] text-amber-700"></i>
                  <span>Matchmaker Verified Dossier: Unredacted inspection privileges enabled.</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 2. DESKTOP 2x2 CARD GRID (Exact Executive SaaS Property Grid Replica) -->
          <div class="pd-info-grid">
            
            <!-- LEFT COLUMN CONTAINER -->
            <div class="flex flex-col gap-4">

              <!-- CARD 0: Verified Contact Information (Exclusive RM View) -->
              <div class="pd-info-card border-l-4 border-l-primary bg-primary/5">
                <div class="pd-card-header text-primary font-bold">
                  <i class="bi bi-telephone-fill text-[17px]"></i> Contact Information (RM Only)
                </div>
                <div class="contact-exclusive-banner mb-3">
                  <i class="bi bi-shield-lock-fill"></i>
                  <span>Confidential contact details — visible exclusively to Matchmakers & RMs.</span>
                </div>
                <div class="pd-property-grid">
                  <div class="pd-prop">
                    <span class="pd-prop-label flex items-center gap-1"><i class="bi bi-telephone text-[12px]"></i> Mobile Number</span>
                    <span class="pd-prop-val font-mono font-bold text-sm text-primary">
                      {{ dossierCandidate.contact?.mobile || '--' }}
                    </span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label flex items-center gap-1"><i class="bi bi-envelope text-[12px]"></i> Email Address</span>
                    <span class="pd-prop-val font-mono text-sm text-slate-800">
                      {{ dossierCandidate.contact?.email || '--' }}
                    </span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label flex items-center gap-1"><i class="bi bi-whatsapp text-[12px] text-emerald-600"></i> WhatsApp Number</span>
                    <span class="pd-prop-val font-mono font-bold text-sm text-emerald-700">
                      {{ dossierCandidate.contact?.whatsapp || dossierCandidate.contact?.mobile || '--' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- CARD 1: Basic & Location Details -->
              <div class="pd-info-card">
                <div class="pd-card-header">
                  <i class="bi bi-person-lines-fill"></i> Basic & Location Details
                </div>
                <div class="pd-property-grid">
                  <div class="pd-prop">
                    <span class="pd-prop-label">City / Location</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.location || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Marital Status</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.marital_status || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Height</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.height || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Mother Tongue</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.mother_tongue || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Physical Status</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.physical_status || 'Normal' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Verification Status</span>
                    <span class="pd-prop-val font-medium text-emerald-700 font-semibold"><i class="bi bi-patch-check-fill"></i> {{ dossierCandidate.verification_status || 'VERIFIED' }}</span>
                  </div>
                </div>
              </div>

              <!-- CARD 2: Education & Profession -->
              <div class="pd-info-card">
                <div class="pd-card-header">
                  <i class="bi bi-briefcase-fill"></i> Education & Profession
                </div>
                <div class="pd-property-grid">
                  <div class="pd-prop">
                    <span class="pd-prop-label">Qualification / Education</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.education || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Occupation</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.occupation || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Employment Sector</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.sector || 'Full-time employment' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Annual Income</span>
                    <span class="pd-prop-val font-medium font-semibold text-slate-900">{{ dossierCandidate.income || 'Not Disclosed' }}</span>
                  </div>
                </div>
              </div>

            </div>

            <!-- RIGHT COLUMN CONTAINER -->
            <div class="flex flex-col gap-4">

              <!-- CARD 3: Religion & Horoscope -->
              <div class="pd-info-card">
                <div class="pd-card-header">
                  <i class="bi bi-stars"></i> Religion & Horoscope
                </div>
                <div class="pd-property-grid">
                  <div class="pd-prop">
                    <span class="pd-prop-label">Religion</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.religion || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Caste / Sect</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.caste || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Sub-Caste</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.sub_caste || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Gothram</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.gothram || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Star / Raasi</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.nakshatra || '--' }} / {{ dossierCandidate.rasi || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Dosham</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.dosham || 'No' }}</span>
                  </div>
                </div>
              </div>

              <!-- CARD 4: Family Details & Lifestyle -->
              <div class="pd-info-card">
                <div class="pd-card-header">
                  <i class="bi bi-house-heart-fill"></i> Family & Lifestyle Details
                </div>
                <div class="pd-property-grid">
                  <div class="pd-prop">
                    <span class="pd-prop-label">Family Type</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.family_type || 'Nuclear' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Family Status</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.family_status || 'Middle Class' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Father's Occupation</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.father_occupation || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Mother's Occupation</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.mother_occupation || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">No. of Brothers</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.no_of_brothers || '0' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">No. of Sisters</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.no_of_sisters || '0' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Diet / Eating Habits</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.diet || '--' }}</span>
                  </div>
                  <div class="pd-prop">
                    <span class="pd-prop-label">Smoking / Drinking</span>
                    <span class="pd-prop-val font-medium">{{ dossierCandidate.smoking || 'No' }} / {{ dossierCandidate.drinking || 'No' }}</span>
                  </div>
                </div>
              </div>

            </div>

          </div><!-- /pd-info-grid -->

        </div><!-- /dossier-body -->

        <!-- Dossier Footer Actions -->
        <div class="dossier-footer" *ngIf="dossierCandidate && !loadingDossier">
          <button class="btn-dossier-bookmark"
                  [class.bookmarked]="dossierCandidate.is_bookmarked"
                  (click)="toggleCandidateBookmark(dossierCandidate)">
            <i class="bi" [ngClass]="dossierCandidate.is_bookmarked ? 'bi-bookmark-star-fill' : 'bi-bookmark-star'"></i>
            {{ dossierCandidate.is_bookmarked ? 'Bookmarked' : 'Bookmark' }}
          </button>
          <div class="dossier-footer-right">
            <button class="btn-dossier-close" (click)="closeDossier()">Close</button>
            <button class="btn-dossier-select"
                    [class.btn-dossier-deselect]="isProfileSelected(dossierCandidate)"
                    (click)="toggleSelectProfile(dossierCandidate)">
              <i class="bi" [ngClass]="isProfileSelected(dossierCandidate) ? 'bi-check-circle-fill' : 'bi-plus-circle-fill'"></i>
              {{ isProfileSelected(dossierCandidate) ? 'Remove from Share' : 'Select & Share' }}
            </button>
          </div>
        </div>

      </div><!-- /dossier-drawer -->

    </div><!-- /console-body -->
  </div><!-- /matchmaking-console -->

</div><!-- /workspace-container -->
